
import { supabase } from '@/integrations/supabase/client';
import { TokenEncryption } from '@/utils/encryption/tokenEncryption';

export interface TokenRefreshResult {
  success: boolean;
  needsReauth: boolean;
  error?: string;
}

export class TokenRefreshService {
  private static readonly TOKEN_REFRESH_THRESHOLD = 60 * 60 * 1000; // 1 hour in milliseconds

  /**
   * Check if tokens need refresh and refresh them if necessary
   */
  static async refreshTokensIfNeeded(userId: string): Promise<TokenRefreshResult> {
    try {
      console.log('Checking token refresh for user:', userId);

      // Get current credentials
      const { data: credentials, error: fetchError } = await supabase
        .from('microsoft365_credentials')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (fetchError || !credentials) {
        console.log('No credentials found for user:', userId);
        return {
          success: false,
          needsReauth: true,
          error: 'No credentials found'
        };
      }

      const expiresAt = new Date(credentials.expires_at);
      const now = new Date();
      const timeUntilExpiry = expiresAt.getTime() - now.getTime();

      // Token has already expired or will expire within threshold
      if (timeUntilExpiry <= this.TOKEN_REFRESH_THRESHOLD) {
        console.log('Token needs refresh, attempting refresh...');
        return await this.performTokenRefresh(userId, credentials);
      }

      console.log('Token is still valid, no refresh needed');
      return {
        success: true,
        needsReauth: false
      };

    } catch (error) {
      console.error('Error checking token refresh:', error);
      return {
        success: false,
        needsReauth: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Perform the actual token refresh
   */
  private static async performTokenRefresh(userId: string, credentials: any): Promise<TokenRefreshResult> {
    try {
      // Decrypt the refresh token
      const decryptedRefreshToken = await TokenEncryption.decryptToken(credentials.refresh_token);

      // Call Microsoft token refresh endpoint
      const response = await fetch('https://login.microsoftonline.com/common/oauth2/v2.0/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: 'your-microsoft-client-id', // This should come from environment
          grant_type: 'refresh_token',
          refresh_token: decryptedRefreshToken,
          scope: credentials.scope,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Token refresh failed:', errorData);
        
        // If refresh token is invalid, user needs to re-authenticate
        if (response.status === 400 || response.status === 401) {
          return {
            success: false,
            needsReauth: true,
            error: 'Refresh token invalid, re-authentication required'
          };
        }

        return {
          success: false,
          needsReauth: false,
          error: `Token refresh failed: ${errorData.error || response.statusText}`
        };
      }

      const tokenData = await response.json();

      // Encrypt new tokens
      const encryptedAccessToken = await TokenEncryption.encryptToken(tokenData.access_token);
      const encryptedRefreshToken = await TokenEncryption.encryptToken(
        tokenData.refresh_token || decryptedRefreshToken // Use new refresh token if provided
      );

      // Update credentials in database
      const expiresAt = new Date(Date.now() + (tokenData.expires_in * 1000)).toISOString();

      const { error: updateError } = await supabase
        .from('microsoft365_credentials')
        .update({
          access_token: encryptedAccessToken,
          refresh_token: encryptedRefreshToken,
          expires_at: expiresAt,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId);

      if (updateError) {
        console.error('Error updating refreshed tokens:', updateError);
        return {
          success: false,
          needsReauth: false,
          error: 'Failed to store refreshed tokens'
        };
      }

      console.log('Tokens refreshed successfully for user:', userId);
      return {
        success: true,
        needsReauth: false
      };

    } catch (error) {
      console.error('Error performing token refresh:', error);
      return {
        success: false,
        needsReauth: false,
        error: error instanceof Error ? error.message : 'Token refresh failed'
      };
    }
  }
}
